import { useEffect, useRef, useState, type KeyboardEvent } from 'react'
import type { Session, SessionPlace } from '../api/types'
import { errorMessage } from '../api/client'
import { uuid4 } from '../api/uuid'
import { useBoardState, useStore } from '../store/context'
import { FolderChoices, issueWorktrees } from './FolderChoices'

export function ChangeFolder({session, disabled}: {session: Session; disabled: boolean}) {
  const store = useStore()
  const dialog = useRef<HTMLElement>(null)
  const { sessions, offline } = useBoardState()
  const [open, setOpen] = useState(false)
  const [place, setPlace] = useState<SessionPlace>({kind: 'worktree'})
  const [sending, setSending] = useState(false)
  const [problem, setProblem] = useState<string | null>(null)
  const blocked = disabled || offline || sending || !!session.pending_place || session.resuming || (session.place?.kind !== 'checkout' && !['idle', 'ended'].includes(session.state)) || (session.queued?.length ?? 0) > 0 || !session.driver || (session.state === 'ended' ? !session.resumable : session.driver.state !== 'acp')
  const existing = issueWorktrees([...sessions.values()], session.project_id, session.issue_id, session.runner_id, session.id)
  const chosen: SessionPlace = place.kind === 'existing' && !existing.some(s => s.id === place.session) ? {kind: 'worktree'} : place
  useEffect(() => {
    if (!open) return
    const previous = document.activeElement as HTMLElement | null
    dialog.current?.querySelector<HTMLInputElement>('input:checked')?.focus()
    return () => previous?.focus?.()
  }, [open])
  const onKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.nativeEvent.isComposing) return
    if (event.key === 'Escape') {
      event.preventDefault()
      event.stopPropagation()
      if (!sending) setOpen(false)
    } else if (event.key === 'Tab' && dialog.current) {
      const stops = [...dialog.current.querySelectorAll<HTMLElement>('input, select, button')].filter(el => !el.matches(':disabled'))
      const first = stops[0], last = stops[stops.length - 1]
      if (event.shiftKey && document.activeElement === first) {event.preventDefault();last?.focus()}
      else if (!event.shiftKey && document.activeElement === last) {event.preventDefault();first?.focus()}
    }
  }
  if (session.origin.kind !== 'started') return null
  const approval = session.place?.kind === 'checkout' && chosen.kind !== 'checkout'
  const move = async () => {
    if (blocked) return
    setSending(true)
    setProblem(null)
    try {
      await store.sendSessionOp({mutation_id: uuid4(), op: 'change_session_folder', args: {session_id: session.id, place: chosen, ...(approval ? {approve_plan: true} : {})}})
      setOpen(false)
    } catch (error) { setProblem(errorMessage(error)) }
    finally { setSending(false) }
  }
  return <>
    {session.place?.kind === 'checkout' && <span className="code-checkout">Read-only in the checkout.</span>}
    <button type="button" className="button button-small" disabled={blocked} onClick={() => {setOpen(true);setProblem(null)}}>{session.pending_place ? 'Changing folder…' : 'Change folder'}</button>
    {open && <div className="palette-backdrop code-dialog-backdrop"><section ref={dialog} className="code-dialog" role="dialog" aria-modal="true" aria-label="Change folder" onKeyDown={onKeyDown}>
      <h2 className="code-dialog-title">Change folder</h2>
      <div className="code-dialog-body">
        <FolderChoices value={chosen} onChange={setPlace} existing={existing} disabled={sending}/>
        <p>The session reopens its conversation in this folder. Once your plan is approved, move to a worktree to edit.</p>
        {problem && <p role="alert" className="text-danger">Could not change folder: {problem}</p>}
      </div>
      <div className="form-actions"><button type="button" className="button" disabled={sending} onClick={() => setOpen(false)}>Cancel</button><button type="button" className="button button-primary" disabled={blocked} onClick={() => void move()}>{sending ? 'Moving…' : approval ? 'Approve plan and move' : 'Move'}</button></div>
    </section></div>}
  </>
}
